import i18n from '@/utils/i18n';
import React from 'react';
import { ApolloError, gql, useMutation } from '@apollo/client';
import { Button, Props as ButtonProps } from '@/components/button';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { getOperationName } from 'apollo-utilities';
import { MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import {
  OrderGuide_SetCollectionMutation,
  OrderGuide_SetCollectionMutationVariables,
} from '@/types';
import { Type } from '@/components/toast';
import { useEditOrderGuideState } from '@/contexts/edit-order-guide-context';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export const SET_COLLECTION_MUTATION = gql`
  mutation OrderGuide_SetCollection(
    $collectionUUID: uuid!
    $collectionName: String
    $partitions: [SetOrderGuideCollectionPartitionInput!]!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    setOrderGuideCollection(
      collection_id: $collectionUUID
      collection_name: $collectionName
      partitions: $partitions
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order_guide_collection_id
    }
  }
`;

type Props = Partial<ButtonProps> & {
  onSaved: () => void;
};

export const OrderGuideEditorSaveButton: React.FC<Props> = ({
  onSaved,
  ...buttonProps
}) => {
  const { collectionName, collectionUUID, partitions } =
    useEditOrderGuideState();
  const { selectedRestaurantRelationship } = useUserContext();
  const { showToast, showErrorToast } = useToasts();

  const [setCollection, { loading }] = useMutation<
    OrderGuide_SetCollectionMutation,
    OrderGuide_SetCollectionMutationVariables
  >(SET_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(MULTI_ORDER_GUIDE_QUERY) as string],
  });

  return (
    <Button
      {...buttonProps}
      disabled={!collectionUUID}
      loading={loading}
      onPress={async () => {
        if (!collectionUUID || !partitions.find((p) => p.isDefault)) {
          return;
        }

        try {
          await setCollection({
            variables: {
              collectionName,
              collectionUUID,
              partitions: partitions.map((partition) => ({
                items: partition.items.map((item) => ({
                  is_minimized: item.isMinimized,
                  par_level: item.parLevel,
                  variant_pack_id:
                    item.itemListOption.metadata!.variantPackUUID,
                })),
                partition_id: partition.partitionUUID,
                partition_name: partition.partitionName,
              })),
              restaurantUUID: selectedRestaurantRelationship.uuid,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
            },
          });

          onSaved();
        } catch (e) {
          if (e instanceof ApolloError && getMessageFromGQLError(e)) {
            showToast({
              autoCloseTimeout: 0,
              content: getMessageFromGQLError(e),
              type: Type.NEGATIVE,
            });
          } else {
            showErrorToast();
          }
        }
      }}
      trackingData={{
        name: 'edit_order_guide.save_button',
        properties: {
          collectionUUID,
        },
      }}
    >
      {i18n.t('done')}
    </Button>
  );
};
